import React, { useEffect, useMemo, useState } from 'react';
import './DoorLockConfig.scss';

const PANEL_WIDTH = 480;

interface FactionRankData {
  id: number;
  name: string;
}

interface FactionData {
  id: string;
  name: string;
  ranks: FactionRankData[];
}

interface IdentityData {
  actorId: number;
  name: string;
  online: boolean;
}

type LockConfig =
  | { kind: 'faction'; factionId: string; minRankId?: number; everyoneCanClose: boolean; code?: string; lockpickable?: boolean; lockLevel?: number }
  | { kind: 'person'; actorIds: number[]; everyoneCanClose: boolean; code?: string; lockpickable?: boolean; lockLevel?: number };

type RefType = 'door' | 'container' | 'other';

// Mirrors doorLock.ts's LOCK_LEVELS - the native ObjectReference.setLockLevel
// Papyrus function's own 0-100 scale (Novice/Apprentice/Adept/Expert/Master
// in 25-unit steps), not the unrelated internal RE::LOCK_LEVEL C++ enum.
const LOCK_LEVEL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Très facile' },
  { value: 25, label: 'Facile' },
  { value: 50, label: 'Moyen' },
  { value: 75, label: 'Difficile' },
  { value: 100, label: 'Très difficile' },
];
const DEFAULT_LOCK_LEVEL = 50;

interface ShowEventDetail {
  refrId: number;
  factions: FactionData[];
  identities: IdentityData[];
  current: LockConfig | null;
  refType?: RefType;
}

const ANY_RANK = '__any__';

const DoorLockConfig: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [refrId, setRefrId] = useState<number | null>(null);
  const [factions, setFactions] = useState<FactionData[]>([]);
  const [identities, setIdentities] = useState<IdentityData[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const [type, setType] = useState<'faction' | 'person'>('faction');
  const [factionId, setFactionId] = useState<string>('');
  const [minRankId, setMinRankId] = useState<string>(ANY_RANK);
  const [selectedActorIds, setSelectedActorIds] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const [everyoneCanClose, setEveryoneCanClose] = useState(true);
  const [code, setCode] = useState('');
  const [refType, setRefType] = useState<RefType>('door');
  const [lockpickable, setLockpickable] = useState(false);
  const [lockLevel, setLockLevel] = useState<number>(DEFAULT_LOCK_LEVEL);

  // A container has no real "closing" activation to gate (closing one
  // happens through the inventory menu, not a second E-press) - the toggle
  // is meaningless there, so it's hidden rather than shown-but-inert.
  const showCloseToggle = refType !== 'container';

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setRefrId(detail.refrId);
      setFactions(detail.factions ?? []);
      setIdentities(detail.identities ?? []);
      setRefType(detail.refType ?? 'door');
      setSearch('');

      const current = detail.current;
      setIsEditing(!!current);
      if (current && current.kind === 'faction') {
        setType('faction');
        setFactionId(current.factionId);
        setMinRankId(current.minRankId === undefined ? ANY_RANK : String(current.minRankId));
        setSelectedActorIds([]);
      } else if (current && current.kind === 'person') {
        setType('person');
        setSelectedActorIds(current.actorIds);
        setFactionId(detail.factions?.[0]?.id ?? '');
        setMinRankId(ANY_RANK);
      } else {
        setType('faction');
        setFactionId(detail.factions?.[0]?.id ?? '');
        setMinRankId(ANY_RANK);
        setSelectedActorIds([]);
      }
      setEveryoneCanClose(current ? current.everyoneCanClose : true);
      setCode(current?.code ?? '');
      setLockpickable(current?.lockpickable ?? false);
      setLockLevel(current?.lockLevel ?? DEFAULT_LOCK_LEVEL);
      setVisible(true);
    };
    window.addEventListener('skymp5-client:showDoorLockConfig', handler);
    return () => window.removeEventListener('skymp5-client:showDoorLockConfig', handler);
  }, []);

  const selectedFaction = useMemo(
    () => factions.find((f) => f.id === factionId) ?? null,
    [factions, factionId]
  );

  const filteredIdentities = useMemo(() => {
    const q = search.trim().toLowerCase();
    return identities.filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [identities, search]);

  const send = (message: string) => {
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(message);
    }
  };

  const close = () => setVisible(false);

  const togglePlayer = (actorId: number) => {
    setSelectedActorIds((prev) =>
      prev.includes(actorId) ? prev.filter((id) => id !== actorId) : [...prev, actorId]
    );
  };

  const submit = () => {
    if (refrId === null) return;

    // Containers have no real "closing" activation to gate - always store
    // true (harmless/inert either way server-side) regardless of the
    // hidden toggle's stale local state.
    const effectiveEveryoneCanClose = showCloseToggle ? everyoneCanClose : true;

    if (type === 'faction') {
      if (!factionId) return;
      const payload = {
        refrId,
        kind: 'faction',
        factionId,
        minRankId: minRankId === ANY_RANK ? null : Number(minRankId),
        everyoneCanClose: effectiveEveryoneCanClose,
        code,
        lockpickable,
        lockLevel,
      };
      send('door-lock-config:' + JSON.stringify(payload));
    } else {
      if (selectedActorIds.length === 0) return;
      const payload = {
        refrId,
        kind: 'person',
        actorIds: selectedActorIds,
        everyoneCanClose: effectiveEveryoneCanClose,
        code,
        lockpickable,
        lockLevel,
      };
      send('door-lock-config:' + JSON.stringify(payload));
    }
    close();
  };

  const removeLock = () => {
    if (refrId === null) return;
    send('door-lock-remove:' + JSON.stringify({ refrId }));
    close();
  };

  if (!visible) return null;

  return (
    <div className="door-lock-overlay">
      <div
        className="door-lock-panel skyrim-panel"
        style={{ width: `${PANEL_WIDTH}px` }}
      >
        <div className="door-lock-header">
          <div className="door-lock-header-title">Configurer le verrou</div>
          <button type="button" className="door-lock-header-close" onClick={close} aria-label="Fermer">×</button>
        </div>

        <div className="door-lock-tabs">
          <button
            type="button"
            className={`door-lock-tab${type === 'faction' ? ' door-lock-tab--active' : ''}`}
            onClick={() => setType('faction')}
          >
            Faction
          </button>
          <button
            type="button"
            className={`door-lock-tab${type === 'person' ? ' door-lock-tab--active' : ''}`}
            onClick={() => setType('person')}
          >
            Personne
          </button>
        </div>

        <div className="door-lock-body">
          {type === 'faction' ? (
            <>
              <label className="door-lock-label">Faction</label>
              <select
                className="door-lock-select"
                value={factionId}
                onChange={(e) => { setFactionId(e.target.value); setMinRankId(ANY_RANK); }}
              >
                {factions.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>

              <label className="door-lock-label">Rang minimum requis pour ouvrir</label>
              <select
                className="door-lock-select"
                value={minRankId}
                onChange={(e) => setMinRankId(e.target.value)}
              >
                <option value={ANY_RANK}>Tous les membres</option>
                {selectedFaction?.ranks.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="door-lock-label">Rechercher un personnage</label>
              <input
                className="door-lock-input"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom du personnage..."
              />

              <div className="door-lock-player-list">
                {filteredIdentities.length === 0 && (
                  <div className="door-lock-empty">Aucun personnage ne correspond.</div>
                )}
                {filteredIdentities.map((p) => (
                  <button
                    type="button"
                    key={p.actorId}
                    className={`door-lock-player${selectedActorIds.includes(p.actorId) ? ' door-lock-player--selected' : ''}${p.online ? '' : ' door-lock-player--offline'}`}
                    onClick={() => togglePlayer(p.actorId)}
                    title={p.online ? 'En ligne' : 'Hors ligne'}
                  >
                    <span className="door-lock-player-bullet" />
                    {p.name}{!p.online ? ' (hors ligne)' : ''}
                  </button>
                ))}
              </div>

              {selectedActorIds.length > 0 && (
                <div className="door-lock-chips">
                  {selectedActorIds.map((id) => {
                    const p = identities.find((op) => op.actorId === id);
                    return (
                      <span className="door-lock-chip" key={id}>
                        {p?.name ?? id}
                        <button type="button" onClick={() => togglePlayer(id)}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}
            </>
          )}

          <div className="door-lock-divider" />

          <label className="door-lock-label">Code d'accès (optionnel)</label>
          <input
            className="door-lock-input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Laisser vide = pas de code"
          />

          {showCloseToggle && (
            <label className="door-lock-toggle">
              <input
                type="checkbox"
                checked={everyoneCanClose}
                onChange={(e) => setEveryoneCanClose(e.target.checked)}
              />
              <span className="door-lock-toggle-box" />
              Tout le monde peut fermer
            </label>
          )}

          <label className="door-lock-toggle">
            <input
              type="checkbox"
              checked={lockpickable}
              onChange={(e) => setLockpickable(e.target.checked)}
            />
            <span className="door-lock-toggle-box" />
            Crochetable (uniquement par une faction autorisée à crocheter)
          </label>

          {lockpickable && (
            <>
              <label className="door-lock-label">Difficulté du crochetage</label>
              <select
                className="door-lock-select"
                value={lockLevel}
                onChange={(e) => setLockLevel(Number(e.target.value))}
              >
                {LOCK_LEVEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </>
          )}
        </div>

        <div className="door-lock-actions">
          {isEditing && (
            <button type="button" className="door-lock-btn door-lock-btn--danger" onClick={removeLock}>
              Retirer le verrou
            </button>
          )}
          <button type="button" className="door-lock-btn door-lock-btn--ghost" onClick={close}>
            Annuler
          </button>
          <button type="button" className="door-lock-btn door-lock-btn--primary" onClick={submit}>
            {isEditing ? 'Mettre à jour' : 'Verrouiller'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoorLockConfig;
